import type { ComponentProps, ReactNode } from 'react'

import { InfoIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'
import { UserMessengerIcon } from '@/components/icons/goodunited-icons'

/*
 * GoodUnited / Metrics. Figma: 10 / Components / Metrics.
 * MetricCard: 8px corners, 12px padding, 3px source accent, Roboto / text-base label,
 *   Poppins / text-3xl / semibold value, optional actions (AvatarGroup, arrow button).
 * MetricValue: label with optional info icon, Poppins / text-4xl / semibold value.
 * MetricSummary: MetricValues in a row with a 24px gap.
 * MetricBadge/Count: Messenger subscriber count on Colors/surface/subtle.
 */
type MetricAccent = 'positive' | 'negative' | 'facebook' | 'messenger' | 'platform' | 'challenges' | 'instagram' | 'other' | 'birthdays' | 'misc'

const accentClasses: Record<MetricAccent, string> = {
    positive: 'bg-chart-positive',
    negative: 'bg-chart-negative',
    facebook: 'bg-chart-facebook',
    messenger: 'bg-chart-messenger',
    platform: 'bg-chart-platform',
    challenges: 'bg-chart-challenges',
    instagram: 'bg-chart-instagram',
    other: 'bg-chart-other',
    birthdays: 'bg-chart-birthdays',
    misc: 'bg-chart-misc'
}

interface MetricCardProps extends ComponentProps<'div'> {
    label: ReactNode
    value: ReactNode
    accent?: MetricAccent
    actions?: ReactNode
    size?: 'default' | 'compact'
}

function MetricCard({ label, value, accent = 'positive', actions, size = 'default', className, ...props }: MetricCardProps) {
    return (
        <div
            data-slot='metric-card'
            className={cn('flex items-center justify-between gap-4 rounded-lg border border-border-default bg-surface-raised p-3', className)}
            {...props}
        >
            <div className='flex min-w-0 items-stretch gap-3'>
                <span className={cn('w-0.75 shrink-0 rounded-full', accentClasses[accent])} aria-hidden='true' />
                <div className='flex min-w-0 flex-col py-0.5'>
                    <span className={cn('truncate text-text-primary', size === 'compact' ? 'text-sm' : 'text-base')}>
                        {label}
                    </span>
                    <span className={cn('font-heading font-semibold text-text-primary', size === 'compact' ? 'text-xl' : 'text-3xl')}>
                        {value}
                    </span>
                </div>
            </div>
            {actions && (
                <div className='flex shrink-0 items-center gap-3'>
                    {actions}
                </div>
            )}
        </div>
    )
}

interface MetricValueProps extends ComponentProps<'div'> {
    label: ReactNode
    value: ReactNode
    info?: string
}

function MetricValue({ label, value, info, className, ...props }: MetricValueProps) {
    return (
        <div data-slot='metric-value' className={cn('flex flex-col gap-2', className)} {...props}>
            <span className='inline-flex items-center gap-1.5 text-base text-text-primary'>
                {label}
                {info && (
                    <span title={info} className='inline-flex text-icon-secondary'>
                        <InfoIcon className='size-4' aria-hidden='true' />
                        <span className='sr-only'>
                            {info}
                        </span>
                    </span>
                )}
            </span>
            <span className='font-heading text-4xl font-semibold text-text-primary'>
                {value}
            </span>
        </div>
    )
}

function MetricSummary({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='metric-summary'
            className={cn('grid grid-cols-1 gap-6 sm:grid-cols-3', className)}
            {...props}
        />
    )
}

interface MetricBadgeCountProps extends ComponentProps<'span'> {
    value: ReactNode
    detail?: ReactNode
    icon?: ReactNode
}

/* MetricBadge/Count, also the count badge used by the source accordion. */
function MetricBadgeCount({ value, detail, icon, className, ...props }: MetricBadgeCountProps) {
    return (
        <span
            data-slot='metric-badge-count'
            className={cn('inline-flex shrink-0 items-center gap-1 rounded-sm border border-border-subtle bg-surface-subtle px-1 py-0.5 text-base text-text-primary [&_svg]:size-4 [&_svg]:shrink-0', className)}
            {...props}
        >
            {icon ?? <UserMessengerIcon className='text-external-messenger' />}
            {value}
            {detail && (
                <span className='text-sm text-text-secondary'>
                    {detail}
                </span>
            )}
        </span>
    )
}

export { MetricBadgeCount, MetricCard, MetricSummary, MetricValue }
export type { MetricAccent }
